import React from 'react'
import { useState } from "react"

import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'

import notFound from "../NotFoundData"
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal"
import {
  removeBankPayment,
  removeCardPayment
} from "../../../../features/payment/paymentSlice"

import { useDispatch, useSelector } from 'react-redux'
import { selectFeatureFlag } from "../../../../features/featureFlags/selectors"
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import {
  changeBillingProcessorInfo,
  changeProcessingMode,
  changePrimaryPaymentMethod,
  recordDebitAuthorization
} from "../../../../features/bankAccountLink/bankAccountLink"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { useIsSupervisor } from "../../../../hooks/user"
import { NavLink } from "react-router-dom"
import Dropdown from "../../../../components/Dropdown/Dropdown"
import { LuEye, LuPencil, LuTrash2, LuShieldAlert } from "react-icons/lu"
import dayjs from "dayjs"
import Input from "../../../../components/Input/Input"
import Calendar from "../../../../components/Calendar/Calendar"
import { convertServerToLocalTime } from "../../../../utils/date"
import { credentialGatedIds, achOnlyIds, shortLabelMap } from '../../../../constants/gatewayRegistry'
import CardVerificationPanel from "../../../../components/CardVerification/CardVerificationPanel"
import { releaseCardVerification, cardVerificationReset } from "../../../../features/cardVerification/cardVerification"
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../../../components/UiKit/UiKitContext"
import { failureOf } from "../../../../utils/errorHelpers"

// A hold only means something while `blockedAt` is set. A CLEARED hold leaves the whole
// subdocument in place — kind, rCode, authorizedAt, the lot — so testing for the object's
// presence would mark every previously-blocked account as blocked for ever.
const liveBlock = (block) => (block?.blockedAt ? block : null)

// A hold with no kind predates the distinction, and every one of those was unauthorized.
const isAdministrativeBlock = (block) => block?.kind === 'administrative'

// Local styled component removed in favor of global theme overrides
//
// `contactDebitBlock` is the CONTACT-level mirror of a NACHA hold, passed in by PaymentMethodTab
// (which reads the feature flag, so this component never sees one). It exists because the mirror
// is what survives the disputed account being deleted: with the account gone, no row carries the
// hold, so nothing explained why the payments had stopped and `Record authorization` — the only
// lawful way out — could not be reached at all.
// WS 2122673. Where the company default decides (the backend sends `companyDefaultGateway`), an
// account's own processor is ignored at charge time unless it is Manual. Offer only those two there.
const ACCOUNT_PROCESSOR_COMPANY_DEFAULT_ONLY_FLAG = 'ACCOUNT_PROCESSOR_COMPANY_DEFAULT_ONLY'

const TableListMethod = ({ triggerUpdate, data, curUserId, userRole, contactDebitBlock, onContactBlockCleared }) => {
  const companyDefaultOnly = useSelector(selectFeatureFlag(ACCOUNT_PROCESSOR_COMPANY_DEFAULT_ONLY_FLAG))
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const isSupervisor = useIsSupervisor()
  const { confirm: kitConfirm } = useUiKit()

  const [idMethod, setIdMethod] = useState(null)

  const [deleteCardMethod, setDeleteCardMethod] = useState(false)
  const [changeDefaultAccount, setChangeDefaultAccount] = useState(false)
  const [deleteBankMethod, setDeleteBankMethod] = useState(false)

  // Clearing a NACHA unauthorized-return block. Gated NARROWER than everything else on this
  // grid, matching the endpoint: DEBIT_AUTHORIZATION_ROLES is ['admin','customer'], so a
  // supervisor — who may change the processor, the mode and the default account — may not do
  // this one. It is an assertion about what the customer said, not an account edit.
  const canRecordDebitAuthorization = userRole === 'customer' || userRole === 'admin'
  const [debitAuthRow, setDebitAuthRow] = useState(null)
  const [debitAuthReference, setDebitAuthReference] = useState('')
  const [debitAuthDate, setDebitAuthDate] = useState(null)
  const [debitAuthNote, setDebitAuthNote] = useState('')
  const [verifyRow, setVerifyRow] = useState(null)
  const [releaseRow, setReleaseRow] = useState(null)

  const apiRef = useGridApiRef()

  const closeDebitAuthModal = () => {
    setDebitAuthRow(null)
    setDebitAuthReference('')
    setDebitAuthDate(null)
    setDebitAuthNote('')
  }


  const calcRemoveModal = (id, type) => {
    setIdMethod(id)
    if (type === 'Card') {
      setDeleteCardMethod(true)
    } else if (type === 'Bank Account') {
      setDeleteBankMethod(true)
    } else {
      setDeleteCardMethod(false)
      setDeleteBankMethod(false)
    }
  }

  const changeDefaultModal = (id) => {
    setIdMethod(id)
    setChangeDefaultAccount(true)
  }

  // Thunks in these handlers reject with rejectWithValue(<string>) and dispatch()
  // never throws for rejected thunks — so success/failure MUST be read off the
  // action's `error` flag (same pattern as ProcessedPayments), never inferred
  // from the payload shape.
  const handleCardDelete = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(removeCardPayment({ userId: curUserId, cardId: idMethod }))
      if (!result.error) {
        showSnackBar("success", "Card successfully deleted")
        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to delete card')
      }

    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const handleChangeDefaultBillingAccount = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(changePrimaryPaymentMethod({ userId: curUserId, billingAccountId: idMethod }))
      if (!result.error) {
        showSnackBar("success", "Default payment method successfully changed")
        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to change default payment method')
      }

    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const handleChangeBillingAccountProcessor = async ({ billingId, processor }) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeBillingProcessorInfo({ billingId, processor }))
      if (!result.error) {
        showSnackBar("success", "Billing processor successfully changed")
        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to change billing processor')
      }

    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const handleChangeProcessingMode = async ({ billingId, mode }) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeProcessingMode({ billingId, mode }))
      if (!result.error) {
        showSnackBar("success", `Processing mode set to ${mode === 'auto' ? 'Auto' : 'Review'}`)
        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to change processing mode')
      }
    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  // The date is OPTIONAL on purpose. The endpoint defaults it to now, which is the normal case —
  // the customer authorised on the call the operator is on. Sending a date only when one was
  // picked keeps the common path from turning a midnight timestamp into an authorisation that
  // predates a block armed earlier the same day, which the endpoint would refuse with a 400.
  const handleRecordDebitAuthorization = async () => {
    const billingId = debitAuthRow?.id
    const reference = debitAuthReference.trim()
    if (!billingId || !reference) return

    triggerLoading(true)
    try {
      const result = await dispatch(recordDebitAuthorization({
        billingId,
        reference,
        authorizedAt: debitAuthDate ? dayjs(debitAuthDate).toISOString() : undefined,
        note: debitAuthNote.trim() || undefined,
      }))
      if (!result.error) {
        // cleared:false is a SUCCESS — the endpoint is idempotent and answers 200 when no block
        // was live. Saying "authorization recorded" either way would leave an operator believing
        // they had unblocked an account that was never blocked.
        const cleared = result?.payload?.data?.cleared
        showSnackBar("success", cleared
          ? "Authorization recorded. The account is no longer blocked."
          : "Authorization recorded. This account had no live block.")

        // TELL THE PARENT, because triggerUpdate cannot fix this one. The contact-level hold
        // arrives here from Contact.jsx's `userData`, which is fetched once per contactId;
        // triggerUpdate only reloads cards and banks. recordDebitAuthorization clears the contact
        // mirror whenever it was live, so after a successful clear our copy of it is stale and the
        // badge would keep announcing a hold that is gone — which an operator reads as the clear
        // having failed, and clicks again.
        if (cleared) onContactBlockCleared?.()

        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to record the authorization')
      }
    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const handleBankDelete = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(removeBankPayment({ userId: curUserId, accountId: idMethod }))
      if (!result.error) {
        showSnackBar("success", "Bank account successfully deleted")
        triggerUpdate()
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to delete bank account')
      }
    } catch (error) {
      console.log('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 50
    },
    {
      "field": "name",
      "headerName": "Method Name ",
      "width": 120,
      "editable": false
      // minWidth guard: flex makes width inert and clamps to MUI's 50px floor, clipping the uppercase "METHOD NAME" header when crowded
      , flex: 1, minWidth: 200, sortable: false,
      // The block lives HERE rather than in the role-gated Action column so that everyone who can
      // see the account can see it is blocked — a supervisor cannot clear it but must not be left
      // wondering why the payments stopped. Only the workspace admin gets the control.
      renderCell: (params) => {
        // The account's OWN hold wins the display when both are live: it is the more specific
        // fact, and it is the one an operator can clear for this account.
        const accountBlock = liveBlock(params?.row?.debitBlock)
        const contactBlock = liveBlock(contactDebitBlock)
        const block = accountBlock || contactBlock
        if (!block) return params.value

        const isAdministrative = isAdministrativeBlock(block)
        const scopeIsContact = !accountBlock

        // WHY BOTH BLOCKS ARE CONSULTED FOR THE BUTTON AND ONLY ONE FOR THE BADGE.
        // recordDebitAuthorization decides whether to refuse by inspecting a single subject's
        // `kind`, so a non-administrative hold on the account beside an ADMINISTRATIVE hold on
        // the contact slips past its refusal and clears the administrative one too. Offering the
        // control in that combination would walk an operator straight into it. The badge still
        // shows the account's own hold, because that part is true.
        const anyAdministrative = isAdministrativeBlock(accountBlock) || isAdministrativeBlock(contactBlock)

        return (
          <div className="flex flex-col gap-1 py-2">
            <span>{params.value}</span>
            <span className="flex items-center gap-1 text-primary-error-600 text-sm-14">
              <LuShieldAlert size={14} />
              Debit blocked
            </span>
            <span className="text-text-secondary text-sm-14">
              {scopeIsContact
                ? 'Contact-level hold'
                : (block.rCode || (isAdministrative ? 'Bank return' : 'Unauthorized return'))} · {convertServerToLocalTime(block.blockedAt)}
            </span>
            {/* A new authorization does not reopen a closed account, so it is not offered here. */}
            {anyAdministrative ? (
              <span className="text-text-secondary text-sm-14">
                Needs replacement account details
              </span>
            ) : canRecordDebitAuthorization && (
              <button
                type="button"
                className="text-left underline cursor-pointer text-sm-14"
                onClick={() => setDebitAuthRow(params.row)}>
                Record authorization
              </button>
            )}
          </div>
        )
      }
    },
    {
      "field": "type",
      "headerName": "Type",
      // bounded content (Card / Bank Account): drop flex so the declared width is honored instead of clamping to the 50px floor
      "width": 180,
      sortable: false
    },
    {
      field: "address",
      headerName: "Billing Address",
      // widest column: keep flex to absorb slack but floor at minWidth so the wrapped address + "BILLING ADDRESS" header never clamp to 50px
      width: 240,
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
          padding: '8px 0',
        }}>
          {params.value}
        </div>
      )
    },
    {
      "field": "lastNumber",
      "headerName": "Number",
      // short content: fixed width clears the uppercase "NUMBER" header instead of flex clamping to 50px
      width: 120, sortable: false
    },
    {
      "field": "cardVerification",
      "headerName": "Verified",
      width: 150,
      sortable: false,
      // The record of which gateway approved the $1-2 check at enrolment. Not the routing
      // processor - that is its own column - and a dash where a card was never verified, so an
      // unverified card reads as unverified rather than as missing data.
      renderCell: (params) => {
        if (params?.row?.type === 'Bank Account') return <span className="text-text-secondary">—</span>

        const verification = params?.row?.cardVerification
        const verified = verification?.verdict === 'passed' && !verification?.supersededAt

        if (verified) {
          return (
            <span className="text-sm text-text-primary">
              {verification.approvedProcessor || verification.approvedGateway || 'verified'}
            </span>
          )
        }

        // No control for anyone who cannot act on it, and none at all while the feature is switched
        // off — the CRM path needs no session token, so the row's own flag is what holds it back.
        if (!permittedRoles || !params?.row?.cardVerificationEnabled) {
          return <span className="text-text-secondary">{verification ? 'Not verified' : '—'}</span>
        }

        // Money out with no verdict is the state a person has to resolve: the sweep reverses what it
        // can reach, and this is the door out of what it cannot.
        if (verification?.unrecoveredCharge) {
          return (
            <button className="text-error text-sm underline" onClick={() => setReleaseRow(params.row)}>
              Money out — release
            </button>
          )
        }

        return (
          <button className="text-primary-600 text-sm underline" onClick={() => setVerifyRow(params.row)}>
            {verification ? 'Not verified — retry' : 'Verify'}
          </button>
        )
      }
    },
    {
      "field": "createdAt",
      "headerName": "Created",
      // date content: fixed width clears the uppercase "CREATED" header instead of flex clamping to 50px
      width: 140,
      sortable: false
    },
    {
      "field": "default",
      "headerName": "Default",
      // single checkbox control: fixed width clears the uppercase "DEFAULT" header instead of flex clamping to 50px
      width: 110,
      renderCell: (params) => {
        return (
          <div>
            <input className="cursor-pointer" type="checkbox" checked={params?.row?.isPrimary}
              onClick={() => changeDefaultModal(params.row.id)} disabled={!permittedRoles} />
          </div>
        )
      },
      sortable: false
    },
  ]

  const permittedRoles = userRole === 'customer' || isSupervisor
  if (permittedRoles) {
    columns.push({
      "field": "processor",
      "headerName": "Processor",
      // Dropdown needs real width: flex + minWidth so the control never clamps to MUI's 50px floor
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const updateProcessor = async (value) => {
          userRole !== 'affiliate' ?
            await handleChangeBillingAccountProcessor({ billingId: params?.row?.id, processor: value }) :
            showSnackBar('error', 'Access denied')

        }

        // DERIVED from src/constants/gatewayRegistry.js + the per-row credential flags the
        // backend sends (`gatewayFlags`, with the legacy `<id>Configured` booleans still
        // present as a fallback). Rail gating comes from the registry's `achOnly` flag, so an
        // ACH-only gateway (green.money, Sola) can never be offered on a card and a DUAL-RAIL
        // one (LawPay, whose card rail is the Mint→LawPay fallback) is never wrongly hidden —
        // the two mistakes the old hand-written list made. The backend enforces the same rule
        // (services/billingAccount/gatewayFlags.js + ACH_ONLY_PROCESSORS) and would reject a
        // card.
        //
        // Shown-when-configured OR already-stored: an account set up before a credential
        // rotation still renders its own processor instead of silently displaying a different
        // one.
        const isBankAccount = params?.row?.type === 'Bank Account'
        const flags = params?.row?.gatewayFlags || {}
        const isConfigured = (id) => Boolean(flags[id] ?? params?.row?.[`${id}Configured`])
        const stored = params?.row?.processor
        const shortLabels = shortLabelMap()

        const optional = credentialGatedIds(isBankAccount ? 'ach' : 'card')
          .filter((id) => (isBankAccount || !achOnlyIds().includes(id)))
          .filter((id) => isConfigured(id) || stored === id)
          .map((id) => ({ label: shortLabels[id], value: id }))

        // An EMPTY stored processor means "inherit the company defaults" (WS 1281163).
        // 'inherit' is the endpoint's explicit clear token — never stored, so it doubles
        // as the display value for an absent processor and the dropdown reads
        // "Company default" instead of blank.
        const companyDefault = companyDefaultOnly ? params?.row?.companyDefaultGateway : null
        const processorItems = companyDefault
          ? [
            { label: `Company default (${shortLabels[companyDefault] || companyDefault})`, value: 'inherit' },
            { label: shortLabels.manual, value: 'manual' },
          ]
          : [
            { label: "Company default", value: 'inherit' },
            { label: shortLabels.mint, value: 'mint' },
            { label: shortLabels.manual, value: 'manual' },
            ...optional,
          ]
        // A stored value other than Manual does nothing while the company default decides, so it
        // reads as the company default.
        const processorValue = companyDefault
          ? (stored === 'manual' ? 'manual' : 'inherit')
          : (stored || 'inherit')

        return (
          <div className="max-h-[24px]">
            <Dropdown
              small={true}
              setValue={updateProcessor}
              value={processorValue}
              menuItems={processorItems}
            />
          </div>
        )
      },
      sortable: false
    })
    columns.push({
      "field": "processingMode",
      "headerName": "Processing",
      // Dropdown needs real width: flex + minWidth so the control never clamps to MUI's 50px floor
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const updateMode = async (value) => {
          userRole !== 'affiliate'
            ? await handleChangeProcessingMode({ billingId: params?.row?.id, mode: value })
            : showSnackBar('error', 'Access denied')
        }
        return (
          <div className="max-h-[24px]">
            <Dropdown
              small={true}
              setValue={updateMode}
              value={params?.row?.processingMode || 'review'}
              menuItems={[
                { label: "Review", value: 'review' },
                { label: "Auto", value: 'auto' },
              ]}
            />
          </div>
        )
      },
      sortable: false
    })
    columns.push({
      "field": "action",
      "headerName": "Action",
      // icon-only actions: fixed width clears the uppercase "ACTION" header instead of flex clamping to 50px
      width: 140,
      renderCell: (params) => {
        const isBankAccount = params.row.type === 'Bank Account'
        const canAccessBankDetails = !isBankAccount || !isSupervisor

        return (
          <div className="w-[100px] flex flex-row justify-end gap-3">
            {canAccessBankDetails && (
              <NavLink
                to={`/billing/${params?.row?.id}/view/${isBankAccount ? 'bank' :
                  params.row.type === 'Card' ? 'card' : 'undefined'}`}>
                <div
                  className="cursor-pointer">
                  <LuEye size={18} className="text-text-secondary" />
                </div>
              </NavLink>
            )}
            {canAccessBankDetails && (
              <NavLink
                to={`/billing/${params?.row?.id}/edit/${isBankAccount ? 'bank' :
                  params.row.type === 'Card' ? 'card' : 'undefined'}`}>
                <div
                  className="cursor-pointer">
                  <LuPencil size={18} className="text-text-secondary" />
                </div>
              </NavLink>
            )}
            <div className="relative cursor-pointer" key={params?.row}
              onClick={() => calcRemoveModal(params.row.id, params.row.type)}>
              <LuTrash2 size={18} className="text-text-secondary" />
            </div>
          </div>
        )
      },
      sortable: false
    })
  }

  const closeVerifyModal = () => {
    setVerifyRow(null)
    dispatch(cardVerificationReset())
  }

  const handleRelease = async () => {
    try {
      triggerLoading(true)
      const result = await dispatch(releaseCardVerification({
        verificationId: releaseRow?.cardVerification?.verificationId,
        billingAccountId: releaseRow?.id,
      }))
      if (result.error) {
        showSnackBar(result.payload?.message || 'Could not release the verification charge', 'error')
      } else {
        showSnackBar('The verification charge has been returned', 'success')
        triggerUpdate()
      }
    } finally {
      triggerLoading(false)
      setReleaseRow(null)
    }
  }


  const cardDeleteOrThrow = async () => {
    const result = await dispatch(removeCardPayment({ userId: curUserId, cardId: idMethod }))
    if (result.error) throw failureOf(result, 'Failed to delete card')
    showSnackBar("success", "Card successfully deleted")
    triggerUpdate()
  }

  const bankDeleteOrThrow = async () => {
    const result = await dispatch(removeBankPayment({ userId: curUserId, accountId: idMethod }))
    if (result.error) throw failureOf(result, 'Failed to delete bank account')
    showSnackBar("success", "Bank account successfully deleted")
    triggerUpdate()
  }

  const releaseOrThrow = async () => {
    const result = await dispatch(releaseCardVerification({
      verificationId: releaseRow?.cardVerification?.verificationId,
      billingAccountId: releaseRow?.id,
    }))
    if (result.error) throw failureOf(result, 'Could not release the verification charge')
    showSnackBar('success', 'The verification charge has been returned')
    triggerUpdate()
  }

  const methodToDelete = data?.find((row) => row.id === idMethod)
  const endingText = methodToDelete?.lastNumber ? ` ending ${methodToDelete.lastNumber}` : ''

  return (
    <div>
      {kitConfirm && (
        <>
          <ConfirmDialog
            open={Boolean(releaseRow)}
            onClose={() => setReleaseRow(null)}
            title={`Return the verification charge on the card ending ${releaseRow?.lastNumber}?`}
            impact={['The client gets the small verification charge back.', 'The card can be verified again.']}
            confirmLabel="Return the charge"
            onConfirm={releaseOrThrow}
          />
          <ConfirmDialog
            open={(userRole === 'customer' || isSupervisor) && deleteCardMethod}
            onClose={() => setDeleteCardMethod(false)}
            title={`Delete the card${endingText}?`}
            impact={['The card is removed from this client.', 'This cannot be undone.']}
            confirmLabel="Delete card"
            tone="danger"
            onConfirm={cardDeleteOrThrow}
          />
          <ConfirmDialog
            open={(userRole === 'customer' || isSupervisor) && deleteBankMethod}
            onClose={() => setDeleteBankMethod(false)}
            title={`Delete the bank account${endingText}?`}
            impact={['The bank account is removed from this client.', 'This cannot be undone.']}
            confirmLabel="Delete bank account"
            tone="danger"
            onConfirm={bankDeleteOrThrow}
          />
        </>
      )}
      {verifyRow &&
        <ConfirmModal
          title={`Verify card ending ${verifyRow?.lastNumber}`}
          modalButtonTitle={"Done"} classes="" open={Boolean(verifyRow)}
          setOpen={(value) => { if (!value) closeVerifyModal() }}
          onClose={closeVerifyModal}
          functionButton={() => { closeVerifyModal(); triggerUpdate() }}>
          <div className="min-w-[380px]">
            <CardVerificationPanel billingAccountId={verifyRow?.id} />
          </div>
        </ConfirmModal>}

      {!kitConfirm && releaseRow &&
        <ConfirmModal leftTopIcon={<LuShieldAlert size={18} className="text-primary-error-600" />}
          title="Return this verification charge?"
          modalButtonTitle={"Release"} classes="" open={Boolean(releaseRow)}
          setOpen={(value) => { if (!value) setReleaseRow(null) }}
          onClose={() => setReleaseRow(null)}
          cancelLeft={true}
          functionButton={handleRelease}>
          <div className="min-w-[380px]">
            The verification charge on the card ending {releaseRow?.lastNumber} was never returned.
            Releasing it refunds the client and frees the card to be verified again.
          </div>
        </ConfirmModal>}

      {!kitConfirm && (userRole === 'customer' || isSupervisor) && deleteCardMethod &&
        <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          title="Are you sure to delete Card method?"
          modalButtonTitle={"Delete"} classes="" open={deleteCardMethod}
          setOpen={setDeleteCardMethod}
          functionButton={handleCardDelete}>
          You will not be able to restore it later
        </ConfirmModal>}

      {(userRole === 'customer' || isSupervisor) && changeDefaultAccount &&
        <ConfirmModal
          title="Warning!"
          modalButtonTitle={"Change"} classes="" open={changeDefaultAccount}
          setOpen={setChangeDefaultAccount}
          functionButton={handleChangeDefaultBillingAccount}>
          Are you sure to change default Payment Method?
        </ConfirmModal>}

      {!kitConfirm && (userRole === 'customer' || isSupervisor) && deleteBankMethod &&
        <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          title="Are you sure to delete Bank method?"
          modalButtonTitle={"Delete"} classes="" open={deleteBankMethod}
          setOpen={setDeleteBankMethod}
          functionButton={handleBankDelete}>
          You will not be able to restore it later
        </ConfirmModal>}

      {canRecordDebitAuthorization && debitAuthRow &&
        <ConfirmModal leftTopIcon={<LuShieldAlert size={18} className="text-primary-error-600" />}
          title="Record debit authorization"
          modalButtonTitle={"Record"} classes="" open={Boolean(debitAuthRow)}
          setOpen={(value) => { if (!value) closeDebitAuthModal() }}
          onClose={closeDebitAuthModal}
          cancelLeft={true}
          disabled={!debitAuthReference.trim()}
          functionButton={handleRecordDebitAuthorization}>
          <div className="flex flex-col gap-4 min-w-[380px]">
            <p>
              {debitAuthRow?.name} ending {debitAuthRow?.lastNumber}. The customer's bank returned a
              debit as unauthorized, so nothing may charge
              {liveBlock(debitAuthRow?.debitBlock) ? ' this account' : ' this contact'} until you
              record that they have authorized us again.
            </p>
            <Input
              label="Evidence reference"
              name="debitAuthReference"
              value={debitAuthReference}
              required
              autoFocus
              placeholder="Call recording id, signed form, ticket"
              onChange={({ target }) => setDebitAuthReference(target.value)}
            />
            <Calendar
              label="Authorization date"
              optional
              disableFuture
              value={debitAuthDate}
              onChange={setDebitAuthDate}
            />
            <p className="text-sm-14">
              Leave the date empty unless you are recording an authorization taken earlier. An empty
              date means now. A date before the return is the authorization the customer disputed,
              and will be refused.
            </p>
            <Input
              label="Note"
              name="debitAuthNote"
              value={debitAuthNote}
              placeholder="Optional"
              onChange={({ target }) => setDebitAuthNote(target.value)}
            />
          </div>
        </ConfirmModal>}

      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={100}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        apiRef={apiRef}
        hideFooterPagination
        disableSelect
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-light' : ''}
      />
    </div>

  )
}

export default TableListMethod
